import { KOMA_COUNT } from '~/constants/koma'
import { timeToKomaNumber } from '~/models/komaToTime'
import type { ColumnSide } from '~/pages/ProcurementPlanDetail/models/columns'

/**
 * 提出管理の取引先カードから渡される不整合範囲 (AC-PPD-022 / 022-1、AC-PPP-004)。
 * 送り側の契約は `SubmissionManagement/models/planDetailUrl.ts` の `buildPlanDetailUrl`
 * (AC-PSM-038-1)。
 */
export type PlanDetailDeeplink = {
  /** 取引先コード (`counterpartyCode`)。名称ではなくコードで列を引く (AC-PPD-022-1) */
  partnerCode: string
  /** 調達側 / 販売側。同じコードが両側の内訳列に出ることがあるので列の選択に使う */
  side: ColumnSide
  /** 範囲の先頭コマの行 index (0-origin、包含) */
  fromIndex: number
  /** 範囲の終端の行 index (0-origin、排他)。コマ 48 まで含む時は 48 */
  toIndexExclusive: number
}

const TIME_PATTERN = /^\d{2}:(00|30)$/

/** "HH:MM" (30 分刻み) を 0:00 からのコマ境界数に変換する。不正なら undefined。 */
function timeToBoundary(time: string): number | undefined {
  if (!TIME_PATTERN.test(time)) return undefined
  // "HH:MM" を開始時刻とするコマ番号 - 1 = その時刻より前のコマ数 ("24:00" → 48)
  const boundary = timeToKomaNumber(time) - 1
  return boundary <= KOMA_COUNT ? boundary : undefined
}

function parseSide(side: string | null): ColumnSide | undefined {
  return side === 'procurement' || side === 'sales' ? side : undefined
}

/**
 * URL クエリ `highlightFrom` / `highlightTo` / `partner` / `side` を解釈する。
 *
 * `highlightTo` は**排他 end** (最終不整合コマの終了時刻、コマ 48 は "24:00")。送り側が
 * `komaEndTime` で組み立てる契約に合わせる (design `plan/submission-detail.html` は包含で
 * 読んでいるが、送り側の実装済み契約を優先する。slice issue #9725 Constraints)。
 *
 * 4 つのどれかが欠けている・形式が不正・範囲が空 (from >= to) の時は undefined を返し、
 * 画面は通常表示にする。
 */
export function parsePlanDetailDeeplink(
  params: URLSearchParams,
): PlanDetailDeeplink | undefined {
  const partnerCode = params.get('partner')?.trim() ?? ''
  const side = parseSide(params.get('side'))
  const from = timeToBoundary(params.get('highlightFrom') ?? '')
  const to = timeToBoundary(params.get('highlightTo') ?? '')
  if (
    partnerCode === '' ||
    side === undefined ||
    from === undefined ||
    to === undefined ||
    from >= to
  ) {
    return undefined
  }
  return { partnerCode, side, fromIndex: from, toIndexExclusive: to }
}
